import { NavLink, useLocation } from "react-router-dom";
import { Home, Settings, User } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { cn } from "../utils/cn";
import type { LucideIcon } from "lucide-react";

interface NavTab {
  id: string;
  label: string;
  icon: LucideIcon;
  route: string;
  matchRoutes: string[];
}

const NAV_TABS: NavTab[] = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    route: "/dashboard",
    matchRoutes: ["/", "/dashboard"],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    route: "/settings",
    matchRoutes: ["/settings"],
  },
  {
    id: "profile",
    label: "Profile",
    icon: User,
    route: "/settings/profile",
    matchRoutes: ["/settings/profile"],
  },
];

function isTabActive(tab: NavTab, pathname: string): boolean {
  // Profile tab takes precedence over Settings for /settings/profile
  if (tab.id === "profile") {
    return pathname === "/settings/profile";
  }
  // Settings tab matches /settings and /settings/* except /settings/profile
  if (tab.id === "settings") {
    return (
      pathname === "/settings" ||
      (pathname.startsWith("/settings/") && pathname !== "/settings/profile")
    );
  }
  // Home tab matches / and /dashboard
  return tab.matchRoutes.includes(pathname);
}

/**
 * NavBar - Mobile bottom navigation bar with route-based active state.
 *
 * Uses React Router NavLink for semantic navigation and Lucide icons.
 * Follows Material 3 "pill indicator" pattern for active tab.
 * Visible only on mobile (below md breakpoint) via CSS.
 */
export const NavBar = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Only show for authenticated users on non-onboarding routes
  if (!user || location.pathname.startsWith("/onboarding")) {
    return null;
  }

  // Don't show on public pages (signin, pricing, legal)
  const publicPaths = [
    "/signin",
    "/pricing",
    "/terms",
    "/privacy",
    "/accessibility",
  ];
  if (publicPaths.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <nav
      className="nav-bar-container"
      id="bottom-nav"
      aria-label="Main navigation"
    >
      {NAV_TABS.map((tab) => {
        const active = isTabActive(tab, location.pathname);
        const Icon = tab.icon;

        return (
          <NavLink
            key={tab.id}
            to={tab.route}
            className={cn("nav-tab", active && "nav-tab--active")}
            aria-label={tab.label}
            aria-current={active ? "page" : undefined}
          >
            <span
              className={cn(
                "nav-tab-icon-container",
                active && "nav-tab-icon-container--active"
              )}
            >
              <Icon className="nav-tab-icon" />
            </span>
            <span
              className={cn("nav-tab-label", active && "nav-tab-label--active")}
            >
              {tab.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
};
